import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import { parseCommandResult } from './command-result.ts';

const urlOptions: QueryUrlOptions = {
  identifier: { type: 'commands' },
  op: 'query',
  resourcePath: 'api/v2/commands/phone-verification',
};

export type PhoneVerificationErrorCode =
  | 'forbidden_principal'
  | 'invalid_phone_number'
  | 'invalid_verification_code'
  | 'missing_verification_code'
  | 'rate_limit_exceeded'
  | 'verification_request_rejected'
  | 'verification_service_unavailable';

const phoneVerificationErrorCodes: ReadonlySet<string> = new Set([
  'forbidden_principal',
  'invalid_phone_number',
  'invalid_verification_code',
  'missing_verification_code',
  'rate_limit_exceeded',
  'verification_request_rejected',
  'verification_service_unavailable',
] satisfies PhoneVerificationErrorCode[]);

export interface PhoneVerificationCommandError {
  type: PhoneVerificationErrorCode;
  detail: string;
}

export interface PhoneVerificationStartResult {
  verificationState: 'awaiting_code';
  destination: { kind: 'phone'; lastFour: string };
}

export interface PhoneVerificationCheckResult {
  verificationState: 'verified';
  phoneNumberId: string;
  phoneNumberChanged: boolean;
}

export interface PhoneVerificationCommandResponse<Result> {
  data: {
    type: 'commands';
    id: string;
    attributes: {
      command: 'start-phone-verification' | 'check-phone-verification';
      status: 'pending' | 'success' | 'error';
      message: string;
      result: Result | { verificationState: 'not_verified' };
      commandErrors: PhoneVerificationCommandError[];
      metadata: { requestedAt: string; requestedBy: string | null };
    };
  };
}

/**
 * Starts a profile phone-verification challenge with POST
 * `/api/v2/commands/phone-verification/start` and skips the request cache.
 * Omitting `phoneNumber` asks the API to use the active mobile number.
 * Consumers can classify a later rejected store request with
 * `phoneVerificationErrorCode`.
 */
export function startPhoneVerificationRequest(phoneNumber?: string): RequestInfo {
  const url = buildBaseURL(urlOptions);
  const body: Record<string, string> = {};
  if (phoneNumber) {
    body['phoneNumber'] = phoneNumber;
  }
  return {
    url: `${url}/start`,
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
    body: JSON.stringify(body),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Checks a 4–10 digit code with POST `/api/v2/commands/phone-verification/check`
 * and skips the request cache. The success envelope reports whether the stored
 * phone number changed. Consumers can classify a later rejected store request
 * with `phoneVerificationErrorCode`.
 */
export function checkPhoneVerificationRequest(code: string, phoneNumber?: string): RequestInfo {
  const url = buildBaseURL(urlOptions);
  const body: Record<string, string> = { code };
  if (phoneNumber) {
    body['phoneNumber'] = phoneNumber;
  }
  return {
    url: `${url}/check`,
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json', Accept: 'application/json' }),
    body: JSON.stringify(body),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Unwraps a successful phone-verification command result for the caller's known command shape.
 *
 * @throws when the response is not a command envelope with a result.
 */
export function parsePhoneVerificationResult<Result>(content: unknown): Result {
  return parseCommandResult<Result>(content);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isPhoneVerificationErrorCode(value: unknown): value is PhoneVerificationErrorCode {
  return typeof value === 'string' && phoneVerificationErrorCodes.has(value);
}

/**
 * Reads the stable capability error code from a command response or WarpDrive
 * request failure. Returns `undefined` for authentication, transport, or malformed
 * errors so callers do not surface provider or transport error text.
 */
export function phoneVerificationErrorCode(error: unknown): PhoneVerificationErrorCode | undefined {
  const content = isRecord(error) && 'content' in error ? error['content'] : error;
  if (
    !isRecord(content) ||
    !isRecord(content['data']) ||
    !isRecord(content['data']['attributes'])
  ) {
    return undefined;
  }

  const errors = content['data']['attributes']['commandErrors'];
  if (
    !Array.isArray(errors) ||
    !isRecord(errors[0]) ||
    !isPhoneVerificationErrorCode(errors[0]['type'])
  ) {
    return undefined;
  }

  return errors[0]['type'];
}
